import {
  BACK_OFFICE_ID_FORMAT_MESSAGE,
  BACK_OFFICE_ID_PATTERN,
  canonicalizeBackOfficeId,
  type CompanyStatus,
} from "@kkp/api-client";
import { z } from "zod";

/**
 * Framework-light pieces shared by the admin and client Clients pages: the
 * sort/filter value types, the pure sort/badge helpers, and the add/edit Zod
 * schemas (plus their inferred form types). The pages keep their JSX in-app
 * because they import the per-app divergent UI components (Input, Dialog, …),
 * and their data layers (`../../api/companies`) diverge (admin handles the
 * create invite warning; client does not), so the react-query/data hooks
 * intentionally stay in each app.
 */

export type SortKey = "name" | "status" | "updatedAt";
export type SortDir = "asc" | "desc";
export type Density = "comfortable" | "compact";

// Exhaustive status→value maps over the generated `CompanyStatus` union. A
// `Record<CompanyStatus, …>` is total by construction, so if the Rust
// `CompanyStatus` enum gains a variant, these object literals stop type-checking
// until the new case is added — the FE half of the Rust↔TS status SSOT (the union
// is single-sourced from Rust, and every site that maps over it stays exhaustive).
const STATUS_SORT_WEIGHT: Record<CompanyStatus, number> = {
  Active: 3,
  Inactive: 1,
};

export function statusWeight(s: CompanyStatus): number {
  return STATUS_SORT_WEIGHT[s];
}

const STATUS_BADGE_CLASS: Record<CompanyStatus, string> = {
  Active: "border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.10)]",
  Inactive: "border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface-2)]",
};

export function statusBadgeClass(status: CompanyStatus): string {
  return STATUS_BADGE_CLASS[status];
}

const buildBackOfficeIdSchema = (minMessage: string) =>
  z
    .string()
    .trim()
    .min(1, minMessage)
    .max(32, "Back-office ID is too long.")
    .regex(BACK_OFFICE_ID_PATTERN, BACK_OFFICE_ID_FORMAT_MESSAGE)
    .transform(canonicalizeBackOfficeId);

export const addCompanySchema = z.object({
  legalName: z
    .string()
    .min(2, "Company legal name is required.")
    .max(64, "Company legal name is too long."),
  backOfficeId: buildBackOfficeIdSchema("Back-office ID is required."),
  taxId: z
    .string()
    .min(2, "Tax ID (NPWP) is required.")
    .max(32, "Tax ID is too long.")
    .regex(
      /^[\d.-]+$/,
      "Tax ID must contain only numbers, dots, and dashes with no spaces",
    ),
  legalAddress: z
    .string()
    .min(5, "Legal address is required.")
    .max(255, "Legal address is too long."),
  sourceBankCode: z
    .string()
    .min(1, "Source bank code is required.")
    .max(3, "Source bank code is too long.")
    .regex(/^\d+$/, "Source bank code must contain only numbers"),
  sourceBankAccount: z
    .string()
    .min(5, "Source bank account is required.")
    .max(24, "Source bank account is too long.")
    .regex(
      /^[\d.-]+$/,
      "Source bank account must contain only numbers, dots, and dashes with no spaces",
    ),
  perBatchLimitIdr: z
    .string()
    .min(1, "Per batch limit is required.")
    .regex(/^\d+(\.\d{1,2})?$/, "Must be a valid number (e.g., 100000000)"),
  perTransactionFeeIdr: z
    .string()
    .min(1, "Per transaction fee is required.")
    .regex(/^\d+(\.\d{1,2})?$/, "Must be a valid number (e.g., 2500)"),
  picFullname: z
    .string()
    .min(2, "PIC fullname is required.")
    .max(64, "PIC fullname is too long."),
  picPhonenumber: z
    .string()
    .min(6, "PIC phone number is required.")
    .max(20, "PIC phone number is too long.")
    .regex(
      /^\+?[1-9]\d{1,14}$/,
      "Must be a valid phone number (e.g., +1234567890)",
    ),
  picEmail: z
    .string()
    .email("PIC email is required.")
    .max(255, "PIC email is too long."),
  reasonDescription: z
    .string()
    .max(255, "Reason description is too long.")
    .optional(),
});

export type AddCompanyForm = z.infer<typeof addCompanySchema>;

const optionalText = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) =>
      typeof value === "string" && value.trim() === "" ? undefined : value,
    schema.optional(),
  );

export const editCompanySchema = z.object({
  legalName: optionalText(
    z
      .string()
      .min(2, "Company legal name is too short.")
      .max(64, "Company legal name is too long."),
  ),
  backOfficeId: optionalText(
    buildBackOfficeIdSchema("Back-office ID is too short."),
  ),
  taxId: optionalText(
    z
      .string()
      .min(2, "Tax ID (NPWP) is too short.")
      .max(32, "Tax ID is too long.")
      .regex(
        /^[\d.-]+$/,
        "Tax ID must contain only numbers, dots, and dashes with no spaces",
      ),
  ),
  legalAddress: optionalText(
    z
      .string()
      .min(5, "Legal address is too short.")
      .max(255, "Legal address is too long."),
  ),
  sourceBankCode: optionalText(
    z
      .string()
      .min(1, "Source bank code is too short.")
      .max(3, "Source bank code is too long.")
      .regex(/^\d+$/, "Source bank code must contain only numbers"),
  ),
  sourceBankAccount: optionalText(
    z
      .string()
      .min(5, "Source bank account is too short.")
      .max(24, "Source bank account is too long.")
      .regex(
        /^[\d.-]+$/,
        "Source bank account must contain only numbers, dots, and dashes with no spaces",
      ),
  ),
  perTransactionFeeIdr: optionalText(
    z
      .string()
      .min(1, "Fee per transaction is required.")
      .regex(/^\d+(\.\d{1,2})?$/, "Must be a valid number (e.g., 2500)"),
  ),
  picFullname: optionalText(
    z
      .string()
      .min(2, "PIC fullname is too short.")
      .max(64, "PIC fullname is too long."),
  ),
  picPhonenumber: optionalText(
    z
      .string()
      .min(6, "PIC phonenumber is too short.")
      .max(20, "PIC phonenumber is too long.")
      .regex(
        /^\+?[1-9]\d{1,14}$/,
        "Must be a valid phone number (e.g., +1234567890)",
      ),
  ),
});

export type EditCompanyForm = z.infer<typeof editCompanySchema>;
